import { Typography } from "@mui/material";
import useMemoizedFn from "ahooks/es/useMemoizedFn";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { AmtDefaultBadge } from "../../../../components";
import { Configuration } from "../../../../config";
import { useBookingNavigation } from "../../../../hooks";
import { usePermissions } from "../../../permissions/hooks";
import { BookedNotification, isDeskBookingNotification } from "../../domain";
import { BookingDetails } from "./BookingDetails";
import { NotificationItem } from "./NotificationItem";

export interface UnbookedNotificationItemProps {
  onClose: () => void;
  notification: BookedNotification;
}

export const UnbookedNotificationItem: React.FC<
  UnbookedNotificationItemProps
> = ({ onClose, notification }) => {
  const { t } = useTranslation("notifications");
  const { bookings } = usePermissions();
  const navigateToBooking = useBookingNavigation();
  const snackbar = useSnackbar();

  const isDeskBooking = isDeskBookingNotification(notification.context);
  const handleClick = useMemoizedFn(() => {
    if (!bookings.canViewBookingsOnDate(notification.context.bookingDate)) {
      snackbar.enqueueSnackbar(t("feedback.expired"), {
        variant: "warning",
      });
      return;
    }
    navigateToBooking({
      date: notification.context.bookingDate,
      locationId: notification.context.location.id,
      ...(isDeskBooking && {
        deskId: notification.context.desk.id,
        floorId: notification.context.floor.id,
      }),
      action: "view",
    });
  });

  const keyPrefix = isDeskBooking ? "desk" : "location";

  return (
    <NotificationItem
      onClick={handleClick}
      onClose={onClose}
      notification={notification}
      badge={
        <AmtDefaultBadge color="red">
          {t(`${keyPrefix}-unbooked.badge`)}
        </AmtDefaultBadge>
      }
      content={
        <>
          <Typography variant="body2" sx={{ mb: 1, mt: 1 }}>
            {isDeskBooking
              ? t(`${keyPrefix}-unbooked.text`)
              : t(`${keyPrefix}-unbooked.text.${Configuration.tenantStyle}`)}
          </Typography>
          <BookingDetails context={notification.context} />
        </>
      }
    />
  );
};
